import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, APP_INITIALIZER, NgModule } from '@angular/core';
import { Observable } from 'rxjs';

/* eslint-disable no-shadow */
var MessageTypes;
(function (MessageTypes) {
    MessageTypes["loadBrowserStorage"] = "LoadBrowserStorage";
    MessageTypes["saveBrowserStorage"] = "SaveBrowserStorage";
})(MessageTypes || (MessageTypes = {}));

const BROWSER_STORAGE_CONFIG = new InjectionToken('BROWSER_STORAGE_CONFIG');

class BrowserStorageService {
    constructor(browserStorageConfig) {
        this.browserStorageConfig = browserStorageConfig;
        // We're storing the entire state at one time. This is really not best practice.
        // There may be an opportunity to optimize. The code will likely become more complex.
        // https://developers.google.com/web/fundamentals/instant-and-offline/web-storage/indexeddb-best-practices
        this.activeWebWorkers = {};
        this.webWorkerInstanceId = 0;
        this.defaultDbVersion = 2;
    }
    /**
     * @param dbConfig
     * @returns observable of true if the indexdb was created successfully
     */
    initialize(dbConfig) {
        this.config = dbConfig;
        this.worker$ = new Observable((observer) => {
            const initialSettings = dbConfig ? dbConfig : this.browserStorageConfig;
            initialSettings.dbInfo.forEach((db) => {
                const dbName = `pan-store__${db.dbName}`;
                const openReq = indexedDB.open(dbName, this.defaultDbVersion);
                // First time setup: create an empty object store
                openReq.addEventListener('success', (dbase) => {
                    observer.next(true);
                    observer.complete();
                });
                openReq.addEventListener('error', (err) => {
                    observer.error(err);
                });
                openReq.onupgradeneeded = (e) => {
                    db.objectStoreNames?.forEach((store) => {
                        if (!openReq.result.objectStoreNames.contains(store)) {
                            openReq.result.createObjectStore(store);
                        }
                    });
                };
            });
        });
        return this.worker$;
    }
    deleteAllDbs() {
        const userAgentString = navigator.userAgent;
        const firefoxAgent = userAgentString.indexOf('Firefox') > -1;
        this.worker$ = new Observable((observer) => {
            const promise = window.indexedDB?.databases();
            promise.then((keys) => {
                keys.forEach((key, i) => {
                    const deleteReq = indexedDB.deleteDatabase(key.name);
                    deleteReq.onsuccess = (event) => {
                        console.warn(`${key.name} deleted successfully`);
                        if (i === keys.length - 1) {
                            observer.next(true);
                        }
                    };
                });
            });
        });
        return this.worker$;
    }
    /**
     *
     * @param dbName (name of database )
     * @param storeEntityName (name of the state slice to store)
     * @param state (data to be saved in the indexDB)
     * @returns Observable message returned by the web worker after saving data
     */
    writeToOfflineStorage(dbName, storeEntityName, state) {
        return this.indexDB(dbName, storeEntityName, '0', state, MessageTypes.saveBrowserStorage);
    }
    /**
     *
     * @param dbName (name of database )
     * @param storeEntityName (name of the state slice to store)
     * @param state (data to be saved in the indexDB)
     * @returns Observable message returned by the web worker after fetching data
     */
    loadFromOfflineStorage(dbName, storeEntityName) {
        return this.indexDB(dbName, storeEntityName, '0', null, MessageTypes.loadBrowserStorage);
    }
    indexDB(dbName, stateEntityName, browserStorageKey, browserStorageData, messageType) {
        this.workerPostMessage = {
            postData: {
                dbName,
                stateEntityName,
                browserStorageKey,
                browserStorageData,
            },
            messageType,
        };
        // const start = window.performance.now();
        return this.webWorkerWrapper(this.workerPostMessage);
    }
    createWebWorkerBlob(workerFunction) {
        return (window.URL ? URL : webkitURL).createObjectURL(new Blob([`onmessage = ${workerFunction};`], {
            type: 'application/javascript',
        }));
    }
    // Web Worker
    webWorkerWrapper(workerPostMessage) {
        //const start = window.performance.now();
        //Keep track of the web workers becausae multiple may be running.
        const instanceId = `wwId${this.webWorkerInstanceId++}`;
        const webWorkerBlob = this.createWebWorkerBlob(webWorkerFunction);
        this.activeWebWorkers[instanceId] = new Worker(webWorkerBlob);
        const currentWorkerInstance = this.activeWebWorkers[instanceId];
        const encrypt = this.config?.encrypt || true;
        //Use the extended interface to add the instanceId.
        const workerPostMessageData = {
            ...workerPostMessage,
            instanceId,
            encrpyt: encrypt,
        };
        //Create an observable to wrap the Web Worker.
        return new Observable((messageObserver) => {
            //Get messages from the Web Worker.
            //We may need different types at some point, but right now the logic is the same for the return.
            currentWorkerInstance.onmessage = (messageEvent) => {
                //Update the subscriber about the message and complete the observerable since we won't get any other returns.
                const postMessageEventData = messageEvent.data;
                //Check to see if we got an error back and message and close the observable.
                if (postMessageEventData?.responseData &&
                    !postMessageEventData?.responseData.error) {
                    messageObserver.next(postMessageEventData.responseData);
                }
                else {
                    messageObserver.error(postMessageEventData?.responseData
                        ? postMessageEventData?.responseData
                        : 'Unknown error occured in Web Worker.');
                }
                messageObserver.complete();
                //Delete the worker from the dictionary of workers to clean up.
                delete this.activeWebWorkers[postMessageEventData?.instanceId];
            };
            currentWorkerInstance.onerror = (error) => {
                //We'll ophan the webworker hanging on activeWebWorkers.
                //Not sure how to get that instanceId back here.
                //This should really be edge case anyway, because exceptions are being handled in the normaly message pipeline above.
                console.error('Web Worker Exception - ', error);
                messageObserver.error(error.message);
            };
            currentWorkerInstance.postMessage(workerPostMessageData);
        });
    }
}
BrowserStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowserStorageService, deps: [{ token: BROWSER_STORAGE_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
BrowserStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowserStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowserStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [BROWSER_STORAGE_CONFIG]
                }] }]; } });
function webWorkerFunction(message) {
    const messageData = message.data;
    if (messageData) {
        const postMessageResponse = {
            instanceId: messageData.instanceId,
            messageType: messageData.messageType,
            responseData: null,
            altData: null,
        };
        //Based on the messageType, run the associated function.
        switch (messageData.messageType) {
            case 'LoadBrowserStorage':
                const putReq = loadBrowserStorageWW(messageData.postData);
                putReq.onSuccess = (result) => {
                    postMessageResponse.altData = messageData.postData.browserStorageKey;
                    postMessageResponse.responseData = result;
                    self.postMessage(postMessageResponse, undefined);
                };
                putReq.onError = (err) => {
                    postMessageResponse.responseData = { error: err.message };
                    console.error('>>> LoadBrowserStorage - Error ', postMessageResponse.responseData);
                    self.postMessage(postMessageResponse, undefined);
                };
                putReq.onComplete = () => close();
                break;
            case 'SaveBrowserStorage':
                const getReq = saveBrowserStorageWW(messageData.postData);
                getReq.onSuccess = () => {
                    postMessageResponse.altData = messageData.postData.browserStorageKey;
                    postMessageResponse.responseData = 'success';
                    self.postMessage(postMessageResponse, undefined);
                };
                getReq.onError = (err) => {
                    postMessageResponse.responseData = { error: err.message };
                    console.error('>>> SaveBrowserStorage - Error ', postMessageResponse.responseData);
                    self.postMessage(postMessageResponse, undefined);
                };
                getReq.onComplete = () => close();
                break;
            default:
                console.error('>>> Unknown Message Type - Error ', messageData);
                // eslint-disable-next-line no-throw-literal
                throw `Unknown Message Type ${messageData.messageType}`;
        }
    }
    function saveBrowserStorageWW(data) {
        const openReq = initBrowserStorageWW(data.dbName, data.stateEntityName);
        const dbResult = {};
        openReq.onsuccess = () => {
            const db = openReq.result;
            const tx = db.transaction(data.stateEntityName, 'readwrite');
            tx.onabort = tx.onerror = () => dbResult.onError(tx.error);
            tx.oncomplete = () => {
                db.close();
                dbResult.onComplete();
            };
            const store = tx.objectStore(data.stateEntityName);
            let putReq;
            if (messageData.encrpyt) {
                putReq = store.put(jsonToBase64(data.browserStorageData), data.browserStorageKey);
            }
            else {
                putReq = store.put(JSON.stringify(data.browserStorageData), data.browserStorageKey);
            }
            putReq.onsuccess = () => dbResult.onSuccess();
            putReq.onerror = () => dbResult.onError(putReq.error);
        };
        openReq.onerror = () => dbResult.onError(openReq.error);
        openReq.onupgradeneeded = (e) => {
            if (!openReq.result.objectStoreNames.contains(data.stateEntityName)) {
                openReq.result.createObjectStore(data.stateEntityName);
            }
        };
        return dbResult;
    }
    function initBrowserStorageWW(dbName, stateEntityName) {
        const openReq = indexedDB.open(`pan-store__${dbName}`, this.defaultDbVersion);
        // First time setup: create an empty object store
        openReq.addEventListener('success', (db) => { });
        openReq.addEventListener('error', (err) => {
            console.error('error', err);
        });
        openReq.onupgradeneeded = (e) => {
            if (!openReq.result.objectStoreNames.contains(stateEntityName)) {
                openReq.result.createObjectStore(stateEntityName);
            }
        };
        return openReq;
    }
    function loadBrowserStorageWW(data) {
        const openReq = initBrowserStorageWW(data.dbName, data.stateEntityName);
        const dbResult = {};
        openReq.onsuccess = () => {
            const db = openReq.result;
            const tx = db.transaction(data.stateEntityName, 'readonly');
            tx.onabort = tx.onerror = () => dbResult.onError(tx.error);
            tx.oncomplete = () => {
                db.close();
                dbResult.onComplete();
            };
            const store = tx.objectStore(data.stateEntityName);
            const getReq = store.get(data.browserStorageKey);
            getReq.onsuccess = () => {
                if (messageData.encrpyt) {
                    try {
                        dbResult.onSuccess(getReq.result ? base64ToJson(getReq.result) : {});
                    }
                    catch (error) {
                        dbResult.onError(error);
                    }
                }
                else {
                    dbResult.onSuccess(getReq.result ? JSON.parse(getReq.result) : {});
                }
            };
            getReq.onerror = () => dbResult.onError(getReq.error);
        };
        openReq.onupgradeneeded = (e) => {
            if (!openReq.result.objectStoreNames.contains(data.stateEntityName)) {
                openReq.result.createObjectStore(data.stateEntityName);
            }
        };
        openReq.onerror = () => dbResult.onError(openReq.error.message);
        return dbResult;
    }
    function jsonToBase64(jsonObject) {
        var jsonString = JSON.stringify(jsonObject);
        var base64String = btoa(encodeURIComponent(jsonString));
        return base64String;
    }
    function base64ToJson(base64String) {
        var jsonString = decodeURIComponent(atob(base64String));
        var jsonObject = JSON.parse(jsonString);
        return jsonObject;
    }
}

class BrowserStorageModule {
    static forRoot(browserStorageConfig) {
        return {
            ngModule: BrowserStorageModule,
            providers: [
                {
                    provide: BROWSER_STORAGE_CONFIG,
                    useValue: browserStorageConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeBrowserStorage,
                    deps: [BrowserStorageService],
                    multi: true,
                },
            ],
        };
    }
}
BrowserStorageModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowserStorageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
BrowserStorageModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: BrowserStorageModule });
BrowserStorageModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowserStorageModule, providers: [BrowserStorageService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowserStorageModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: [],
                    providers: [BrowserStorageService],
                }]
        }] });
function initializeBrowserStorage(browserStorageService) {
    const storage = () => browserStorageService.initialize();
    return storage;
}

/*
 * Public API Surface of browser-storage
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BrowserStorageModule, BrowserStorageService, MessageTypes, initializeBrowserStorage };
